<script setup lang="ts">
import { computed, ref } from 'vue';
import { isAxiosError } from 'axios';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import { toast } from 'vue-sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import CoverArtTile from '@/components/CoverArtTile.vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { postLiveTrackActionRetry } from '@/composables/api/useCrudApi';
import { usePermissions } from '@/composables/usePermissions';
import type { LiveTrackAction } from '@/ts';
import { formatUsMmDdYyTime12h } from '@/utils/date';
import { deriveActionLabel, actionStatusTone, statusLabelForId } from './liveTrackActionDisplay';
import { readUpdateFieldsAlbumName } from './updateFieldsFormatter';
import UpdatedFieldsPopover from './UpdatedFieldsPopover.vue';

/**
 * One `LiveTrackAction` rendered as a card in the History Inspector.
 *
 * Shows the derived action label, the result/error, who performed it and
 * when. The track block mirrors `AudioFileIssueCard` — display string (title +
 * remix), artist, album — and the display string is a button: clicking it
 * drills into that track's full action history (the parent stacks a new
 * inspector view).
 */
const props = defineProps<{
  action: LiveTrackAction;
  /** When true, hide Retry even if the backend still reports `can_retry` (e.g. immediately after queueing). */
  retrySuppressed?: boolean;
}>();

const emit = defineEmits<{
  (e: 'drilldown', liveTrackId: number, title: string): void;
  /** Emitted after the backend accepts the retry — the parent refetches the history list. */
  (e: 'retried', actionId: number): void;
}>();

/**
 * The History Inspector's search payload nests only a lightweight track
 * reference — see `LiveTrackAction::toSearchableArray()` in the tabasco
 * backend. It is `id` + `display_string` (the title-and-remix string) plus the
 * `artist` / `album` `{ id, display_string }` refs the backend folds in for
 * this card; it is NOT a full `LiveTrack`, so type it narrowly here rather than
 * leaning on the much wider `LiveTrack` interface.
 */
interface DisplayStringRef {
  id?: number | null;
  display_string?: string | null;
}
interface HistoryTrackRef {
  id?: number | null;
  display_string?: string | null;
  artist?: DisplayStringRef | null;
  album?: DisplayStringRef | null;
  cover_art_signed_url?: string | null;
}

const actionLabel = computed(() => deriveActionLabel(props.action));

const liveTrack = computed<HistoryTrackRef | null>(
  () => (props.action.live_track as HistoryTrackRef | null | undefined) ?? null,
);

/**
 * The track's display string — title and remix, built by the backend's
 * `LiveTrack::getDisplayStringAttribute()`. Falls back to the action's stored
 * track code, then a generic label.
 */
const trackDisplayString = computed(
  () =>
    liveTrack.value?.display_string?.trim() ||
    props.action.live_track_code ||
    'Unknown track',
);

const artistName = computed(() => liveTrack.value?.artist?.display_string?.trim() || null);

/**
 * For Field-update actions the album in `update_fields` is the *applied* album
 * — that's the most accurate label for what the action did. The nested
 * `live_track.album` is only a point-in-time snapshot from when the document
 * was indexed (which may pre-date the action's processing), so prefer the
 * payload value and fall back to the snapshot when there isn't one (rollback /
 * audio-file / cover-art actions don't carry an album in `update_fields`).
 */
const albumName = computed(
  () =>
    readUpdateFieldsAlbumName(props.action.update_fields) ||
    liveTrack.value?.album?.display_string?.trim() ||
    null,
);

// A point-in-time snapshot from when the action was indexed; CoverArtTile falls
// back to its placeholder if the signed URL has since expired.
const coverArtUrl = computed(() => liveTrack.value?.cover_art_signed_url ?? null);

const drilldownTrackId = computed(
  () => props.action.live_track_id ?? liveTrack.value?.id ?? null,
);

const statusId = computed(() => props.action.live_track_action_status_id ?? null);

const statusLabel = computed(() => statusLabelForId(statusId.value));

const tone = computed(() => actionStatusTone(statusId.value));

const toneClass = computed(() => {
  switch (tone.value) {
    case 'success':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300';
    case 'error':
      return 'bg-destructive/15 text-destructive';
    case 'pending':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
});

/** Whether the action carries an error message (surfaced in the status tooltip). */
const hasError = computed(() => !!props.action.error_messages);

/**
 * Who performed the action — from the nested `{ id, display_string }`
 * `performed_by_user` reference (same lightweight shape as the track ref).
 * Null when the ES document carries no nested user, so the card omits the
 * "by" line rather than showing a meaningless label.
 */
const performedBy = computed(() => {
  const user = props.action.performed_by_user as DisplayStringRef | null | undefined;
  return user?.display_string?.trim() || null;
});

const timestamp = computed(() =>
  formatUsMmDdYyTime12h(props.action.created_at),
);

function onTrackClick() {
  if (drilldownTrackId.value != null) {
    emit('drilldown', drilldownTrackId.value, trackDisplayString.value);
  }
}

const { can } = usePermissions();

/**
 * The backend computes {@code can_retry} on the resource: the action's own
 * status must be a terminal failure, AND no later action on the same track may
 * be queued, in flight, or succeeded. Retry also requires {@code can_create} on
 * {@code live_track_action} — same bit as Track Matching Save / bulk-create
 * (see PageModelPermissionDefinitionSeeder on the BE).
 */
const canRetry = computed(
  () =>
    props.action.can_retry === true &&
    !props.retrySuppressed &&
    can('create', 'live_track_action'),
);
const isRetrying = ref(false);

async function onRetryClick() {
  if (!canRetry.value || isRetrying.value) {
    return;
  }
  isRetrying.value = true;
  try {
    await postLiveTrackActionRetry(props.action.id);
    toast.success('Retry queued.');
    emit('retried', props.action.id);
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === 403) {
      toast.error('You do not have permission to retry live track actions.');
    } else if (isAxiosError(error) && error.response?.status === 422) {
      toast.error('This action can no longer be retried.');
    } else {
      toast.error('Could not retry this action.');
    }
  } finally {
    isRetrying.value = false;
  }
}
</script>

<template>
  <Card
    class="border-border/80 flex flex-col gap-2 p-3 shadow-sm"
    data-testid="history-action-card"
  >
    <!-- Headline: action label + status, timestamp pinned right. -->
    <div class="flex items-start justify-between gap-2">
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="bg-muted rounded px-1.5 py-0.5 text-xs font-semibold leading-none">
          {{ actionLabel }}
        </span>
        <Tooltip>
          <TooltipTrigger as-child>
            <span
              class="rounded px-1.5 py-0.5 text-xs font-medium leading-none cursor-help"
              :class="toneClass"
              data-testid="history-action-card-status"
            >
              {{ statusLabel }}
            </span>
          </TooltipTrigger>
          <TooltipContent class="max-w-md text-xs">
            <div class="whitespace-pre-wrap break-words">
              <div class="font-medium mb-1">{{ statusLabel }}</div>
              <div
                v-if="hasError"
                class="max-h-48 overflow-auto whitespace-pre-wrap break-words text-xs"
                data-testid="history-action-card-error"
              >
                {{ action.error_messages }}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
      <!--
        Right rail: timestamp on top, "Show updated fields" trigger right
        beneath it. The popover hides itself when `update_fields` is empty (the
        common case for rollback / audio-file / cover-art actions), so this
        column degrades to just the date for those.
      -->
      <div class="flex shrink-0 flex-col items-end gap-1">
        <span
          class="text-muted-foreground font-mono text-[11px] leading-none tabular-nums"
        >
          {{ timestamp }}
        </span>
        <UpdatedFieldsPopover :update-fields="action.update_fields" />
      </div>
    </div>

    <!--
      Track block: cover + display string / artist / album — the same shape as
      AudioFileIssueCard. The display string is a button that drills into the
      track's full action history.

      The cover spans exactly the three text lines — its top aligns with the
      display string, its bottom with the album. `items-stretch` on the row +
      `justify-between` on the text column does that: the 48px cover sets the
      row height and the text column stretches to match. The cover wrapper is
      `flex` (not a plain block) so the inline-block CoverArtTile does not sit in a
      line box that would add descender space below it.
    -->
    <div class="flex items-stretch gap-2">
      <div class="flex shrink-0">
        <CoverArtTile
          :src="coverArtUrl"
          :alt="`Cover art for ${trackDisplayString}`"
          :size="48"
        />
      </div>
      <div class="flex min-w-0 flex-col justify-between">
        <button
          type="button"
          class="text-primary hover:underline truncate text-left text-sm font-medium leading-tight disabled:cursor-default disabled:text-foreground disabled:no-underline"
          data-testid="history-action-card-track"
          :disabled="drilldownTrackId == null"
          :title="`See all actions on ${trackDisplayString}`"
          @click="onTrackClick"
        >
          {{ trackDisplayString }}
        </button>
        <span
          class="text-foreground truncate text-xs leading-tight"
          data-testid="history-action-card-artist"
        >
          {{ artistName ?? 'Unknown artist' }}
        </span>
        <span
          class="text-muted-foreground truncate text-[11px] leading-tight"
          data-testid="history-action-card-album"
        >
          {{ albumName ?? 'Unknown album' }}
        </span>
      </div>
    </div>

    <!-- Inline error field removed; error is shown in the status tooltip when present. -->

    <!--
      Footer: "by <user>" on the left, retry button on the right. The retry
      button only renders when the backend's `can_retry` flag is true (terminal
      failure AND no later queued / in-flight / success for the same track —
      see `LiveTrackAction::getCanRetryAttribute()` on the BE).
    -->
    <div
      v-if="performedBy || canRetry"
      class="mt-1 flex items-center justify-between gap-2"
    >
      <p v-if="performedBy" class="text-muted-foreground text-[11px] leading-none">
        by {{ performedBy }}
      </p>
      <span v-else />
      <Button
        v-if="canRetry"
        type="button"
        variant="outline"
        size="sm"
        class="h-7 gap-1 px-2 text-xs"
        :disabled="isRetrying"
        data-testid="history-action-card-retry"
        @click="onRetryClick"
      >
        <ArrowPathIcon class="h-3.5 w-3.5" :class="{ 'animate-spin': isRetrying }" />
        {{ isRetrying ? 'Retrying…' : 'Retry' }}
      </Button>
    </div>
  </Card>
</template>
